import { Controller } from "@hotwired/stimulus";
import Swiper from "swiper";
import {
  Navigation,
  Pagination,
  Controller as SwiperControllerModule,
} from "swiper/modules";
import type { SwiperOptions } from "swiper/types";
import { application } from "src/stimulus/application";

// landIeul: components/uiParts/Carousel / commons/Carousel 相当の汎用 swiper 初期化。
// 対象の .swiper 要素に付与して使う:
//   data-controller="site--swiper"
//   data-site--swiper-config-value: SwiperOptions の JSON。
//     navigation は true (自要素内の .swiper-button-prev/next を使用) か
//     {prevEl, nextEl, disabledClass} (セレクタ文字列。"@self " prefix で自要素スコープ、
//     それ以外は Next 同様 document 全体から解決。カンマ区切りで複数指定可)。
//   data-site--swiper-group-value + data-site--swiper-role-value(main|thumbs):
//     DescriptionImages 相当の本体/サムネ相互リンク (Controller module)。
//   data-site--swiper-active-class-value: サムネ slide 直下に付与する active クラス名
//     (本体の slideChange で同期。サムネクリックで本体を slideToLoop)。
type SwiperGroup = { main?: SiteSwiperController; thumbs?: SiteSwiperController };
const swiperGroups = new Map<string, SwiperGroup>();

class SiteSwiperController extends Controller<HTMLElement> {
  static values = {
    config: Object,
    group: { type: String, default: "" },
    role: { type: String, default: "main" },
    activeClass: { type: String, default: "" },
  };

  declare readonly configValue: SwiperOptions;
  declare readonly groupValue: string;
  declare readonly roleValue: string;
  declare readonly activeClassValue: string;

  swiper?: Swiper;

  connect(): void {
    const config = {
      modules: [Navigation, Pagination, SwiperControllerModule],
      ...this.configValue,
    } as SwiperOptions;
    if (this.configValue.navigation) {
      config.navigation = this.resolveNavigation(this.configValue.navigation);
    }
    this.swiper = new Swiper(this.element, config);

    if (this.groupValue) {
      const group = swiperGroups.get(this.groupValue) || {};
      group[this.roleValue as "main" | "thumbs"] = this;
      swiperGroups.set(this.groupValue, group);
      if (group.main?.swiper && group.thumbs?.swiper) {
        this.linkGroup(group as Required<SwiperGroup>);
      }
    }
  }

  disconnect(): void {
    if (this.groupValue) swiperGroups.delete(this.groupValue);
    this.swiper?.destroy(true, false);
    this.swiper = undefined;
  }

  private resolveNavigation(nav: SwiperOptions["navigation"]): SwiperOptions["navigation"] {
    if (!nav) return nav;
    const scoped = (sel: string) => Array.from(this.element.querySelectorAll<HTMLElement>(sel));
    if (nav === true) {
      return { prevEl: scoped(".swiper-button-prev"), nextEl: scoped(".swiper-button-next") };
    }
    const resolve = (sel: unknown) => {
      if (typeof sel !== "string") return sel as HTMLElement[] | null | undefined;
      const els: HTMLElement[] = [];
      sel.split(",").forEach((rawPart) => {
        const part = rawPart.trim();
        if (part.startsWith("@self ")) {
          els.push(...scoped(part.slice("@self ".length)));
        } else {
          els.push(...Array.from(document.querySelectorAll<HTMLElement>(part)));
        }
      });
      return els;
    };
    return { ...nav, prevEl: resolve(nav.prevEl), nextEl: resolve(nav.nextEl) };
  }

  // DescriptionImages 相当: 本体/サムネを相互制御し、active サムネ強調とサムネクリック遷移を再現
  private linkGroup(group: Required<SwiperGroup>): void {
    const main = group.main.swiper!;
    const thumbs = group.thumbs.swiper!;
    main.controller.control = thumbs;
    thumbs.controller.control = main;

    const activeClass = group.thumbs.activeClassValue;
    const thumbSlides = () =>
      Array.from(group.thumbs.element.querySelectorAll<HTMLElement>(".swiper-slide"));
    const syncActive = () => {
      if (!activeClass) return;
      thumbSlides().forEach((slide) => {
        const isActive = Number(slide.dataset.swiperSlideIndex ?? -1) === main.realIndex;
        slide.firstElementChild?.classList.toggle(activeClass, isActive);
      });
    };
    main.on("slideChange", syncActive);
    syncActive();

    thumbSlides().forEach((slide) => {
      slide.addEventListener("click", () => {
        const index = Number(slide.dataset.swiperSlideIndex ?? -1);
        if (index >= 0) main.slideToLoop(index);
      });
    });
  }
}

application.register("site--swiper", SiteSwiperController);
